import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';

import { getApiUrl } from '../../utils/apiUtils';

const STATUS_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'archived', label: 'Archived' },
];

const SectionCard = ({ title, children, actions }) => (
  <section className="rounded-xl border border-gray-800 bg-gray-900/70 p-4 space-y-3">
    <div className="flex items-center justify-between">
      <p className="text-sm font-semibold uppercase tracking-wide text-gray-400">{title}</p>
      {actions}
    </div>
    {children}
  </section>
);

const Tag = ({ label }) => (
  <span className="inline-flex items-center rounded-full border border-blue-500/50 bg-blue-500/10 px-2 py-0.5 text-xs text-blue-200">
    {label}
  </span>
);

const prettyJson = (payload) => JSON.stringify(payload, null, 2);

const FeedbackDetailDrawer = ({ submissionId, onClose, onUpdated = () => {} }) => {
  const { getToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submission, setSubmission] = useState(null);
  const [statusValue, setStatusValue] = useState('new');
  const [assignedToValue, setAssignedToValue] = useState('');
  const [noteValue, setNoteValue] = useState('');
  const [workflowError, setWorkflowError] = useState('');
  const [workflowMessage, setWorkflowMessage] = useState('');
  const [isSavingWorkflow, setIsSavingWorkflow] = useState(false);

  const fetchDetail = useCallback(async () => {
    if (!submissionId) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = await getToken();
      const response = await fetch(`${getApiUrl()}/admin/feedback/${submissionId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Unable to load feedback details.');
      }

      const payload = await response.json();
      setSubmission(payload);
    } catch (detailError) {
      setError(detailError.message || 'Unexpected error loading submission.');
    } finally {
      setLoading(false);
    }
  }, [submissionId, getToken]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  useEffect(() => {
    if (submission) {
      setStatusValue(submission.status || 'new');
      setAssignedToValue(submission.assigned_to || '');
      setNoteValue('');
    }
  }, [submission]);

  const handleWorkflowSave = useCallback(async () => {
    if (!submission) {
      return;
    }

    const trimmedNote = noteValue.trim();
    const hasStatusChange = statusValue !== submission.status;
    const hasAssignmentChange = (assignedToValue || '') !== (submission.assigned_to || '');
    const hasNote = Boolean(trimmedNote);

    if (!hasStatusChange && !hasAssignmentChange && !hasNote) {
      setWorkflowMessage('No changes to save.');
      setWorkflowError('');
      return;
    }

    // Validate note length
    if (trimmedNote.length > 10000) {
      setWorkflowError(`Note exceeds the 10,000 character limit (${trimmedNote.length.toLocaleString()} characters). Please shorten your note.`);
      setWorkflowMessage('');
      return;
    }

    setWorkflowError('');
    setWorkflowMessage('');
    setIsSavingWorkflow(true);

    try {
      const token = await getToken();
      const payload = {};
      if (hasStatusChange) {
        payload.status = statusValue;
      }
      if (hasAssignmentChange) {
        payload.assigned_to = assignedToValue || null;
      }
      if (hasNote) {
        payload.note = trimmedNote;
      }

      const response = await fetch(`${getApiUrl()}/admin/feedback/${submission.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        // Log validation errors to console for debugging
        if (response.status === 422) {
          const errorData = await response.json().catch(() => ({}));
          console.error('❌ [VALIDATION ERROR]', errorData);
          console.error('❌ [VALIDATION ERROR] Request payload:', payload);
          console.error('❌ [VALIDATION ERROR] Full error details:', JSON.stringify(errorData, null, 2));
        }
        throw new Error('Unable to update workflow. Please try again.');
      }

      const updated = await response.json();
      setSubmission(updated);
      setNoteValue('');
      setWorkflowMessage('Workflow updated.');
      setWorkflowError('');
      onUpdated();
    } catch (updateError) {
      setWorkflowError(updateError.message || 'Unexpected error while saving.');
    } finally {
      setIsSavingWorkflow(false);
    }
  }, [submission, assignedToValue, noteValue, statusValue, getToken, onUpdated]);

  const contextKeys = useMemo(() => {
    if (!submission?.context_snapshot) {
      return [];
    }
    return Object.keys(submission.context_snapshot);
  }, [submission]);

  const statusDisplay = useMemo(() => submission?.status?.replace('_', ' '), [submission]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} role="presentation" />
      <div className="relative h-full max-h-[90vh] w-full max-w-4xl overflow-y-auto bg-gray-950 border border-gray-800 rounded-lg shadow-2xl">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-900 bg-gray-950/95 px-6 py-4">
          <div>
            <p className="text-xs uppercase tracking-wider text-gray-500">Feedback #{submissionId}</p>
            <h2 className="text-xl font-semibold text-white">{submission?.title || 'Loading…'}</h2>
            {submission?.status && (
              <p className="text-sm text-gray-400">
                Status:&nbsp;
                <span className="font-medium text-white capitalize">{statusDisplay}</span>
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-gray-400 transition hover:bg-gray-900 hover:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            aria-label="Close detail drawer"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>

        <div className="px-6 py-6 space-y-6">
          {loading && (
            <div className="rounded-xl border border-gray-800 bg-gray-900/70 p-4 text-sm text-gray-300">Loading details…</div>
          )}
          {error && (
            <div className="rounded-xl border border-red-500/40 bg-red-900/20 p-4 text-sm text-red-100">{error}</div>
          )}

          {!loading && submission && (
            <>
              <SectionCard
                title="Workflow"
                actions={
                  workflowMessage && (
                    <span className="text-xs text-emerald-300">{workflowMessage}</span>
                  )
                }
              >
                {workflowError && (
                  <div className="rounded-lg border border-red-500/40 bg-red-900/20 p-2 text-xs text-red-100">
                    {workflowError}
                  </div>
                )}
                <div className="grid gap-4 md:grid-cols-2">
                  <label className="flex flex-col space-y-2">
                    <span className="text-xs uppercase tracking-wide text-gray-500">Status</span>
                    <select
                      value={statusValue}
                      onChange={(event) => setStatusValue(event.target.value)}
                      className="rounded-lg border border-gray-800 bg-black/40 px-3 py-2 text-sm text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {STATUS_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="flex flex-col space-y-2">
                    <span className="text-xs uppercase tracking-wide text-gray-500">Assign to</span>
                    <input
                      type="email"
                      placeholder="team@runium.app"
                      value={assignedToValue}
                      onChange={(event) => setAssignedToValue(event.target.value)}
                      className="rounded-lg border border-gray-800 bg-black/40 px-3 py-2 text-sm text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </label>
                </div>
                <label className="mt-4 block">
                  <span className="text-xs uppercase tracking-wide text-gray-500">Internal note</span>
                  <textarea
                    rows={3}
                    value={noteValue}
                    onChange={(event) => setNoteValue(event.target.value)}
                    placeholder="Add context for the team (optional)"
                    className="mt-2 w-full rounded-lg border border-gray-800 bg-black/40 px-3 py-2 text-sm text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <div className={`mt-1 text-right text-xs ${noteValue.length > 10000 ? 'text-red-400' : noteValue.length > 9000 ? 'text-yellow-400' : 'text-gray-500'}`}>
                    {noteValue.length.toLocaleString()} / 10,000 characters
                  </div>
                </label>
                <div className="flex flex-wrap items-center gap-3 pt-3">
                  <button
                    type="button"
                    onClick={handleWorkflowSave}
                    disabled={isSavingWorkflow}
                    className="inline-flex items-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:cursor-not-allowed disabled:bg-blue-900"
                  >
                    {isSavingWorkflow && (
                      <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white/80 border-t-transparent" />
                    )}
                    Save workflow updates
                  </button>
                  <p className="text-xs text-gray-500">
                    Status + assignment sync instantly with history + notes.
                  </p>
                </div>
              </SectionCard>

              <SectionCard title="Summary">
                <dl className="grid grid-cols-2 gap-4 text-sm text-gray-300">
                  <div>
                    <dt className="text-gray-500 uppercase tracking-wide text-xs">Type</dt>
                    <dd className="font-medium text-white capitalize">{submission.feedback_type}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 uppercase tracking-wide text-xs">Submitted</dt>
                    <dd>
                      {submission.created_at
                        ? new Intl.DateTimeFormat('en-US', {
                            dateStyle: 'medium',
                            timeStyle: 'short',
                          }).format(new Date(submission.created_at))
                        : '—'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 uppercase tracking-wide text-xs">Account tier</dt>
                    <dd className="font-medium text-white">{submission.user?.account_tier || 'unknown'}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 uppercase tracking-wide text-xs">Locale</dt>
                    <dd>{submission.user?.locale || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 uppercase tracking-wide text-xs">Assigned to</dt>
                    <dd>{submission.assigned_to || 'Unassigned'}</dd>
                  </div>
                  <div className="col-span-2">
                    <dt className="text-gray-500 uppercase tracking-wide text-xs">Description</dt>
                    <dd className="mt-1 whitespace-pre-wrap text-gray-100">{submission.description}</dd>
                  </div>
                </dl>
                <div className="flex flex-wrap gap-2 pt-3">
                  {submission.flags?.is_duplicate && <Tag label="Duplicate" />}
                  {submission.flags?.is_archived && <Tag label="Archived" />}
                  {submission.include_activity && <Tag label="Includes activity" />}
                  {!!submission.attachments?.length && <Tag label={`${submission.attachments.length} attachment(s)`} />}
                </div>
              </SectionCard>

              {!!submission.attachments?.length && (
                <SectionCard title="Attachments">
                  <ul className="divide-y divide-gray-800 text-sm text-gray-300">
                    {submission.attachments.map((attachment) => (
                      <li key={`${attachment.filename}-${attachment.tag}`} className="py-2 flex items-center justify-between">
                        <div>
                          <p className="font-medium text-white">{attachment.filename}</p>
                          <p className="text-xs text-gray-500">
                            {attachment.mime_type} · tag: {attachment.tag || '—'}
                          </p>
                        </div>
                        {attachment.storage_url && (
                          <button
                            onClick={async () => {
                              try {
                                const token = await getToken();
                                const filename = attachment.storage_url.replace('feedback/', '');
                                const response = await fetch(`${getApiUrl()}/feedback/attachment/${filename}`, {
                                  headers: {
                                    Authorization: `Bearer ${token}`,
                                  },
                                });
                                
                                if (!response.ok) {
                                  throw new Error('Failed to load attachment');
                                }
                                
                                const blob = await response.blob();
                                const blobUrl = URL.createObjectURL(blob);
                                const newWindow = window.open(blobUrl, '_blank');
                                
                                // Clean up blob URL after a delay to allow the window to load
                                setTimeout(() => {
                                  URL.revokeObjectURL(blobUrl);
                                }, 1000);
                                
                                if (!newWindow) {
                                  // Fallback: try to download if popup blocked
                                  const downloadUrl = URL.createObjectURL(blob);
                                  const a = document.createElement('a');
                                  a.href = downloadUrl;
                                  a.download = attachment.filename || 'attachment';
                                  document.body.appendChild(a);
                                  a.click();
                                  document.body.removeChild(a);
                                  setTimeout(() => URL.revokeObjectURL(downloadUrl), 1000);
                                }
                              } catch (error) {
                                alert(`Error opening attachment: ${error.message}`);
                              }
                            }}
                            className="text-blue-400 text-xs hover:underline cursor-pointer"
                          >
                            Open
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                </SectionCard>
              )}

              {submission.activity_summary && (
                <SectionCard
                  title="Activity Summary"
                >
                  <div className="space-y-3">
                    {(() => {
                      const summary = submission.activity_summary;
                      const recentActivity = summary.recent_activity || {};
                      const chats = summary.chats || recentActivity.chats_last_7d || 0;
                      const workouts = recentActivity.workouts_logged || 0;
                      
                      return (
                        <dl className="grid grid-cols-2 gap-4 text-sm">
                          {chats > 0 && (
                            <div>
                              <dt className="text-gray-500 text-xs mb-1">Chats (Last 7 days)</dt>
                              <dd className="text-gray-200 font-semibold">{chats}</dd>
                            </div>
                          )}
                          {workouts > 0 && (
                            <div>
                              <dt className="text-gray-500 text-xs mb-1">Workouts Logged</dt>
                              <dd className="text-gray-200 font-semibold">{workouts}</dd>
                            </div>
                          )}
                          {chats === 0 && workouts === 0 && (
                            <div className="col-span-2">
                              <p className="text-sm text-gray-400 italic">No recent activity</p>
                            </div>
                          )}
                        </dl>
                      );
                    })()}
                  </div>
                </SectionCard>
              )}

              {submission.client_meta && Object.keys(submission.client_meta).length > 0 && (
                <SectionCard
                  title="Client Metadata"
                  actions={
                    <span className="text-xs text-gray-500">
                      {Object.keys(submission.client_meta).length} fields
                    </span>
                  }
                >
                  <div className="space-y-4">
                    {(() => {
                      const meta = submission.client_meta;
                      // Handle backward compatibility: check for nested structure
                      const isNested = meta.client_meta && typeof meta.client_meta === 'object';
                      const nestedMeta = isNested ? meta.client_meta : {};
                      
                      // For flattened structure, use meta directly; for nested, extract request fields
                      const requestIp = meta.request_ip || meta.ip;
                      const requestPort = meta.request_port || meta.port;
                      const requestPath = meta.request_path || meta.path;
                      const requestReferer = meta.request_referer || meta.referer;
                      const requestAcceptLanguage = meta.request_accept_language || meta.accept_language;
                      const requestUserAgent = meta.request_user_agent || meta.user_agent;
                      
                      return (
                        <>
                          {/* Request Info */}
                          {(requestIp || requestPath || requestPort || requestReferer || requestAcceptLanguage) && (
                            <div className="rounded-lg border border-gray-800 bg-black/40 p-3">
                              <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">Request Info</h4>
                              <dl className="grid grid-cols-2 gap-3 text-sm">
                                {requestIp && (
                                  <div>
                                    <dt className="text-gray-500 text-xs">IP Address</dt>
                                    <dd className="text-gray-200 font-mono text-xs">{requestIp}</dd>
                                  </div>
                                )}
                                {requestPort && (
                                  <div>
                                    <dt className="text-gray-500 text-xs">Port</dt>
                                    <dd className="text-gray-200">{requestPort}</dd>
                                  </div>
                                )}
                                {requestPath && (
                                  <div className="col-span-2">
                                    <dt className="text-gray-500 text-xs">Path</dt>
                                    <dd className="text-gray-200 font-mono text-xs">{requestPath}</dd>
                                  </div>
                                )}
                                {requestReferer && (
                                  <div className="col-span-2">
                                    <dt className="text-gray-500 text-xs">Referer</dt>
                                    <dd className="text-gray-200 text-xs truncate" title={requestReferer}>
                                      {requestReferer.length > 80 ? `${requestReferer.substring(0, 80)}...` : requestReferer}
                                    </dd>
                                  </div>
                                )}
                                {requestAcceptLanguage && (
                                  <div className="col-span-2">
                                    <dt className="text-gray-500 text-xs">Accept Language</dt>
                                    <dd className="text-gray-200 text-xs">{requestAcceptLanguage}</dd>
                                  </div>
                                )}
                              </dl>
                            </div>
                          )}

                          {/* Client Info */}
                          {(meta.client_session_id || nestedMeta.session_id || meta.client_clerk_user_id || nestedMeta.clerk_user_id || 
                            meta.client_user_email || nestedMeta.user_email || meta.client_locale || nestedMeta.locale || 
                            meta.client_platform || nestedMeta.platform || meta.client_context_source || nestedMeta.context_source) && (
                            <div className="rounded-lg border border-gray-800 bg-black/40 p-3">
                              <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">Client Info</h4>
                              <dl className="grid grid-cols-2 gap-3 text-sm">
                                {(meta.client_session_id || nestedMeta.session_id) && (
                                  <div>
                                    <dt className="text-gray-500 text-xs">Session ID</dt>
                                    <dd className="text-gray-200 font-mono text-xs">{meta.client_session_id || nestedMeta.session_id}</dd>
                                  </div>
                                )}
                                {(meta.client_clerk_user_id || nestedMeta.clerk_user_id) && (
                                  <div>
                                    <dt className="text-gray-500 text-xs">Clerk User ID</dt>
                                    <dd className="text-gray-200 font-mono text-xs truncate" title={meta.client_clerk_user_id || nestedMeta.clerk_user_id}>
                                      {(meta.client_clerk_user_id || nestedMeta.clerk_user_id).length > 25 ? `${(meta.client_clerk_user_id || nestedMeta.clerk_user_id).substring(0, 25)}...` : (meta.client_clerk_user_id || nestedMeta.clerk_user_id)}
                                    </dd>
                                  </div>
                                )}
                                {(meta.client_user_email || nestedMeta.user_email) && (
                                  <div>
                                    <dt className="text-gray-500 text-xs">User Email</dt>
                                    <dd className="text-gray-200">{meta.client_user_email || nestedMeta.user_email}</dd>
                                  </div>
                                )}
                                {(meta.client_locale || nestedMeta.locale || meta.locale) && (
                                  <div>
                                    <dt className="text-gray-500 text-xs">Locale</dt>
                                    <dd className="text-gray-200">{meta.client_locale || nestedMeta.locale || meta.locale}</dd>
                                  </div>
                                )}
                                {(meta.client_timezone_offset_minutes !== undefined || nestedMeta.timezone_offset_minutes !== undefined) && (
                                  <div>
                                    <dt className="text-gray-500 text-xs">Timezone</dt>
                                    <dd className="text-gray-200">
                                      {(() => {
                                        const offset = meta.client_timezone_offset_minutes !== undefined 
                                          ? meta.client_timezone_offset_minutes 
                                          : nestedMeta.timezone_offset_minutes;
                                        // Convert minutes to hours
                                        const hours = Math.floor(offset / 60);
                                        const minutes = offset % 60;
                                        const sign = offset >= 0 ? '+' : '-';
                                        const absHours = Math.abs(hours);
                                        const timezoneStr = `UTC${sign}${absHours}${minutes !== 0 ? `:${String(Math.abs(minutes)).padStart(2, '0')}` : ''}`;
                                        return `${timezoneStr} (${offset} min)`;
                                      })()}
                                    </dd>
                                  </div>
                                )}
                                {(meta.client_platform || nestedMeta.platform) && (
                                  <div>
                                    <dt className="text-gray-500 text-xs">Platform</dt>
                                    <dd className="text-gray-200">{meta.client_platform || nestedMeta.platform}</dd>
                                  </div>
                                )}
                                {(meta.client_context_source || nestedMeta.context_source) && (
                                  <div>
                                    <dt className="text-gray-500 text-xs">Context Source</dt>
                                    <dd className="text-gray-200 capitalize">{meta.client_context_source || nestedMeta.context_source}</dd>
                                  </div>
                                )}
                                {(meta.include_activity_summary !== undefined || meta.client_include_activity !== undefined || nestedMeta.include_activity !== undefined) && (
                                  <div>
                                    <dt className="text-gray-500 text-xs">Include Activity</dt>
                                    <dd className="text-gray-200">
                                      {(meta.include_activity_summary || meta.client_include_activity || nestedMeta.include_activity || 
                                        meta.client_include_activity === 'True' || meta.client_include_activity === 'true' ||
                                        nestedMeta.include_activity === 'True' || nestedMeta.include_activity === 'true') ? (
                                        <span className="text-emerald-400">Yes</span>
                                      ) : (
                                        <span className="text-gray-400">No</span>
                                      )}
                                    </dd>
                                  </div>
                                )}
                                {(meta.client_generated_at || nestedMeta.generated_at) && (
                                  <div className="col-span-2">
                                    <dt className="text-gray-500 text-xs">Generated At</dt>
                                    <dd className="text-gray-200">
                                      {(() => {
                                        const dateStr = meta.client_generated_at || nestedMeta.generated_at;
                                        try {
                                          const date = new Date(dateStr);
                                          if (isNaN(date.getTime())) {
                                            return dateStr; // Return raw string if invalid
                                          }
                                          return date.toLocaleString(undefined, {
                                            dateStyle: 'medium',
                                            timeStyle: 'long',
                                          });
                                        } catch (e) {
                                          return dateStr; // Return raw string on error
                                        }
                                      })()}
                                    </dd>
                                  </div>
                                )}
                                {((meta.client_viewport && typeof meta.client_viewport === 'object') || 
                                  (nestedMeta.viewport && typeof nestedMeta.viewport === 'object')) && (
                                  <div className="col-span-2">
                                    <dt className="text-gray-500 text-xs mb-1">Viewport</dt>
                                    <dd className="text-gray-200">
                                      {(() => {
                                        const viewport = meta.client_viewport || nestedMeta.viewport;
                                        return viewport.width && viewport.height 
                                          ? `${viewport.width} × ${viewport.height}px`
                                          : viewport.width 
                                            ? `${viewport.width} × ?px`
                                            : '—';
                                      })()}
                                    </dd>
                                  </div>
                                )}
                                {(meta.client_user_agent || requestUserAgent || nestedMeta.user_agent) && (
                                  <div className="col-span-2">
                                    <dt className="text-gray-500 text-xs mb-1">User Agent</dt>
                                    <dd className="text-gray-200 text-xs">
                                      <details className="cursor-pointer">
                                        <summary className="hover:text-gray-300 truncate" title={meta.client_user_agent || requestUserAgent || nestedMeta.user_agent}>
                                          {(meta.client_user_agent || requestUserAgent || nestedMeta.user_agent || '').length > 80 
                                            ? `${(meta.client_user_agent || requestUserAgent || nestedMeta.user_agent).substring(0, 80)}...` 
                                            : (meta.client_user_agent || requestUserAgent || nestedMeta.user_agent)}
                                        </summary>
                                        <pre className="mt-2 max-h-32 overflow-auto rounded bg-black/60 p-2 text-xs font-mono whitespace-pre-wrap break-words">
                                          {meta.client_user_agent || requestUserAgent || nestedMeta.user_agent}
                                        </pre>
                                      </details>
                                    </dd>
                                  </div>
                                )}
                              </dl>
                            </div>
                          )}

                          {/* Fallback for any unexpected fields */}
                          {Object.keys(meta).filter(key => {
                            const handledKeys = [
                              'request_ip', 'request_port', 'request_path', 'request_referer', 'request_accept_language', 'request_user_agent',
                              'client_session_id', 'client_clerk_user_id', 'client_user_email', 'client_locale', 'client_timezone_offset_minutes',
                              'client_platform', 'client_context_source', 'client_include_activity', 'client_generated_at', 'client_viewport',
                              'client_user_agent', 'include_activity_summary'
                            ];
                            return !handledKeys.includes(key) && typeof meta[key] !== 'object';
                          }).length > 0 && (
                            <details className="rounded-lg border border-gray-800 bg-black/40 p-3">
                              <summary className="text-xs font-semibold uppercase tracking-wide text-gray-400 cursor-pointer hover:text-gray-300">
                                Additional Metadata
                              </summary>
                              <pre className="mt-3 max-h-48 overflow-auto rounded bg-black/60 p-3 text-xs text-gray-300">
                                {prettyJson(Object.fromEntries(
                                  Object.entries(meta).filter(([key]) => {
                                    const handledKeys = [
                                      'request_ip', 'request_port', 'request_path', 'request_referer', 'request_accept_language', 'request_user_agent',
                                      'client_session_id', 'client_clerk_user_id', 'client_user_email', 'client_locale', 'client_timezone_offset_minutes',
                                      'client_platform', 'client_context_source', 'client_include_activity', 'client_generated_at', 'client_viewport',
                                      'client_user_agent', 'include_activity_summary'
                                    ];
                                    return !handledKeys.includes(key) && typeof meta[key] !== 'object';
                                  })
                                ))}
                              </pre>
                            </details>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </SectionCard>
              )}

              {submission.context_snapshot && (
                <SectionCard
                  title="Context Snapshot"
                  actions={<span className="text-xs text-gray-500">{contextKeys.length} sections</span>}
                >
                  <div className="space-y-4 max-h-[600px] overflow-y-auto">
                    {(() => {
                      const ctx = submission.context_snapshot;
                      
                      return (
                        <>
                          {/* UI Context */}
                          {ctx.ui && (
                            <div className="rounded-lg border border-gray-800 bg-black/40 p-3">
                              <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">UI Context</h4>
                              <dl className="grid grid-cols-2 gap-3 text-sm">
                                {ctx.ui.view && (
                                  <div>
                                    <dt className="text-gray-500 text-xs">Current View</dt>
                                    <dd className="text-gray-200 capitalize">{ctx.ui.view}</dd>
                                  </div>
                                )}
                                {ctx.ui.active_conversation_id && (
                                  <div>
                                    <dt className="text-gray-500 text-xs">Active Conversation</dt>
                                    <dd className="text-gray-200">#{ctx.ui.active_conversation_id}</dd>
                                  </div>
                                )}
                                {ctx.ui.defaults && (
                                  <div className="col-span-2">
                                    <dt className="text-gray-500 text-xs mb-1">Form Defaults</dt>
                                    <dd className="text-gray-200">
                                      {ctx.ui.defaults.title && <div>Title: {ctx.ui.defaults.title}</div>}
                                      {ctx.ui.defaults.feedback_type && (
                                        <div className="capitalize">Type: {ctx.ui.defaults.feedback_type}</div>
                                      )}
                                    </dd>
                                  </div>
                                )}
                                {ctx.ui.recent_actions && ctx.ui.recent_actions.length > 0 && (
                                  <div className="col-span-2">
                                    <dt className="text-gray-500 text-xs mb-1">Recent Actions</dt>
                                    <dd className="text-gray-200">
                                      <div className="flex flex-wrap gap-1">
                                        {ctx.ui.recent_actions.map((action, idx) => (
                                          <span key={idx} className="inline-flex items-center rounded border border-gray-700 bg-gray-800/50 px-2 py-0.5 text-xs">
                                            {action}
                                          </span>
                                        ))}
                                      </div>
                                    </dd>
                                  </div>
                                )}
                              </dl>
                            </div>
                          )}

                          {/* Client Info */}
                          {ctx.client && (
                            <div className="rounded-lg border border-gray-800 bg-black/40 p-3">
                              <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">Client Info</h4>
                              <dl className="grid grid-cols-2 gap-3 text-sm">
                                {ctx.client.device && (
                                  <div>
                                    <dt className="text-gray-500 text-xs">Device</dt>
                                    <dd className="text-gray-200">{ctx.client.device}</dd>
                                  </div>
                                )}
                                {ctx.client.os && (
                                  <div>
                                    <dt className="text-gray-500 text-xs">OS</dt>
                                    <dd className="text-gray-200 text-xs truncate" title={ctx.client.os}>
                                      {ctx.client.os.length > 50 ? `${ctx.client.os.substring(0, 50)}...` : ctx.client.os}
                                    </dd>
                                  </div>
                                )}
                                {ctx.client.locale && (
                                  <div>
                                    <dt className="text-gray-500 text-xs">Locale</dt>
                                    <dd className="text-gray-200">{ctx.client.locale}</dd>
                                  </div>
                                )}
                                {ctx.client.app_version && (
                                  <div>
                                    <dt className="text-gray-500 text-xs">App Version</dt>
                                    <dd className="text-gray-200">{ctx.client.app_version}</dd>
                                  </div>
                                )}
                                {ctx.client.timezone_offset_minutes && (
                                  <div>
                                    <dt className="text-gray-500 text-xs">Timezone Offset</dt>
                                    <dd className="text-gray-200">{ctx.client.timezone_offset_minutes} min</dd>
                                  </div>
                                )}
                                {ctx.client.network && (
                                  <div className="col-span-2">
                                    <dt className="text-gray-500 text-xs mb-1">Network</dt>
                                    <dd className="text-gray-200">
                                      <div className="flex flex-wrap gap-2">
                                        <span className="capitalize">{ctx.client.network.state || 'unknown'}</span>
                                        {ctx.client.network.latency_ms && (
                                          <span className="text-gray-400">· {ctx.client.network.latency_ms}ms</span>
                                        )}
                                        {ctx.client.network.effective_type && (
                                          <span className="text-gray-400">· {ctx.client.network.effective_type}</span>
                                        )}
                                      </div>
                                    </dd>
                                  </div>
                                )}
                              </dl>
                            </div>
                          )}

                          {/* Account Info */}
                          {ctx.account && (
                            <div className="rounded-lg border border-gray-800 bg-black/40 p-3">
                              <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">Account</h4>
                              <dl className="grid grid-cols-2 gap-3 text-sm">
                                {ctx.account.tier && (
                                  <div>
                                    <dt className="text-gray-500 text-xs">Tier</dt>
                                    <dd className="text-gray-200 capitalize">{ctx.account.tier}</dd>
                                  </div>
                                )}
                                {ctx.account.onboard_complete !== undefined && (
                                  <div>
                                    <dt className="text-gray-500 text-xs">Onboarding</dt>
                                    <dd className="text-gray-200">
                                      {ctx.account.onboard_complete ? (
                                        <span className="text-emerald-400">Complete</span>
                                      ) : (
                                        <span className="text-yellow-400">Incomplete</span>
                                      )}
                                    </dd>
                                  </div>
                                )}
                              </dl>
                            </div>
                          )}

                          {/* Workout Info */}
                          {ctx.workout && (
                            <div className="rounded-lg border border-gray-800 bg-black/40 p-3">
                              <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">Workout Schedule</h4>
                              <dl className="space-y-2 text-sm">
                                {ctx.workout.week_starts && (
                                  <div>
                                    <dt className="text-gray-500 text-xs">Week Starts</dt>
                                    <dd className="text-gray-200">
                                      {new Date(ctx.workout.week_starts).toLocaleDateString(undefined, {
                                        dateStyle: 'medium',
                                      })}
                                    </dd>
                                  </div>
                                )}
                                {ctx.workout.next_titles && ctx.workout.next_titles.length > 0 && (
                                  <div>
                                    <dt className="text-gray-500 text-xs mb-1">Upcoming Workouts</dt>
                                    <dd className="text-gray-200">
                                      <ul className="list-disc list-inside space-y-1">
                                        {ctx.workout.next_titles.map((title, idx) => (
                                          <li key={idx}>{title}</li>
                                        ))}
                                      </ul>
                                    </dd>
                                  </div>
                                )}
                                {ctx.workout.recent_completions && ctx.workout.recent_completions.length > 0 && (
                                  <div>
                                    <dt className="text-gray-500 text-xs mb-1">Recent Completions</dt>
                                    <dd className="text-gray-200">
                                      <ul className="list-disc list-inside space-y-1">
                                        {ctx.workout.recent_completions.map((completion, idx) => (
                                          <li key={idx}>{completion}</li>
                                        ))}
                                      </ul>
                                    </dd>
                                  </div>
                                )}
                              </dl>
                            </div>
                          )}

                          {/* Conversation */}
                          {ctx.conversation_context && ctx.conversation_context.messages && Array.isArray(ctx.conversation_context.messages) && ctx.conversation_context.messages.length > 0 && (
                            <div className="rounded-lg border border-gray-800 bg-black/40 p-3">
                              <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">
                                Conversation Context ({ctx.conversation_context.messages.length} messages)
                                {ctx.conversation_context.conversation_id && (
                                  <span className="ml-2 text-gray-500">(ID: {ctx.conversation_context.conversation_id})</span>
                                )}
                              </h4>
                              {ctx.conversation_context.coach_persona && (
                                <div className="mb-3 text-xs text-gray-400">
                                  Coach: {ctx.conversation_context.coach_persona.name || 'Unknown'}
                                  {ctx.conversation_context.coach_persona.id && ` (ID: ${ctx.conversation_context.coach_persona.id})`}
                                </div>
                              )}
                              <ul className="space-y-2">
                                {ctx.conversation_context.messages.map((msg, idx) => (
                                  <li key={idx} className="rounded border border-gray-700 bg-gray-900/60 p-2">
                                    <div className="flex items-start justify-between gap-3">
                                      <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                          <span className={`text-xs font-semibold capitalize ${
                                            msg.role === 'user' ? 'text-blue-300' : 'text-purple-300'
                                          }`}>
                                            {msg.role}
                                          </span>
                                        </div>
                                        <p className="text-sm text-gray-200 whitespace-pre-wrap">
                                          {msg.preview || '(No preview)'}
                                        </p>
                                      </div>
                                      {msg.timestamp && (
                                        <time className="text-xs text-gray-500 whitespace-nowrap">
                                          {new Date(msg.timestamp).toLocaleString(undefined, {
                                            dateStyle: 'short',
                                            timeStyle: 'short',
                                          })}
                                        </time>
                                      )}
                                    </div>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {/* User Profile */}
                          {ctx.user_profile && (
                            <div className="rounded-lg border border-gray-800 bg-black/40 p-3">
                              <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">User Profile</h4>
                              <dl className="space-y-3 text-sm">
                                {ctx.user_profile.onboarding_completed !== undefined && (
                                  <div>
                                    <dt className="text-gray-500 text-xs">Onboarding</dt>
                                    <dd className="text-gray-200">
                                      {ctx.user_profile.onboarding_completed ? (
                                        <span className="text-emerald-400">Complete</span>
                                      ) : (
                                        <span className="text-yellow-400">Incomplete</span>
                                      )}
                                    </dd>
                                  </div>
                                )}
                                {ctx.user_profile.target_races && (
                                  <div>
                                    <dt className="text-gray-500 text-xs mb-1">Target Races</dt>
                                    <dd className="text-gray-200">
                                      {ctx.user_profile.target_races.distance && (
                                        <div>Distance: {ctx.user_profile.target_races.distance}</div>
                                      )}
                                      {ctx.user_profile.target_races.raceDate && (
                                        <div>Race Date: {ctx.user_profile.target_races.raceDate}</div>
                                      )}
                                      {ctx.user_profile.target_races.targetTime && (
                                        <div>Target Time: {(() => {
                                          const totalSeconds = ctx.user_profile.target_races.targetTime.totalSeconds;
                                          if (!totalSeconds) return '—';
                                          const hours = Math.floor(totalSeconds / 3600);
                                          const minutes = Math.floor((totalSeconds % 3600) / 60);
                                          return `${hours}:${String(minutes).padStart(2, '0')}`;
                                        })()}</div>
                                      )}
                                    </dd>
                                  </div>
                                )}
                                {ctx.user_profile.weekly_volume && (
                                  <div>
                                    <dt className="text-gray-500 text-xs mb-1">Weekly Volume</dt>
                                    <dd className="text-gray-200">
                                      {ctx.user_profile.weekly_volume.value} {ctx.user_profile.weekly_volume.unit}
                                      {ctx.user_profile.weekly_volume.minutes && ` · ${ctx.user_profile.weekly_volume.minutes} min`}
                                    </dd>
                                  </div>
                                )}
                                {ctx.user_profile.personal_bests && (
                                  <div>
                                    <dt className="text-gray-500 text-xs mb-1">Personal Bests</dt>
                                    <dd className="text-gray-200 text-xs">
                                      {ctx.user_profile.personal_bests.recentPBs && Object.keys(ctx.user_profile.personal_bests.recentPBs).length > 0 && (
                                        <div className="mb-1">
                                          <span className="text-gray-400">Recent: </span>
                                          {Object.entries(ctx.user_profile.personal_bests.recentPBs).map(([key, value]) => {
                                            // Format as MM:SS (assuming value is in seconds as string or number)
                                            const seconds = typeof value === 'string' ? parseInt(value, 10) : value;
                                            const mins = Math.floor(seconds / 60);
                                            const secs = seconds % 60;
                                            const formatted = `${mins}:${String(secs).padStart(2, '0')}`;
                                            return <span key={key} className="mr-2">{key}: {formatted}</span>;
                                          })}
                                        </div>
                                      )}
                                      {ctx.user_profile.personal_bests.lifetimePBs && Object.keys(ctx.user_profile.personal_bests.lifetimePBs).length > 0 && (
                                        <div>
                                          <span className="text-gray-400">Lifetime: </span>
                                          {Object.entries(ctx.user_profile.personal_bests.lifetimePBs).map(([key, value]) => {
                                            // Format as MM:SS (assuming value is in seconds as string or number)
                                            const seconds = typeof value === 'string' ? parseInt(value, 10) : value;
                                            const mins = Math.floor(seconds / 60);
                                            const secs = seconds % 60;
                                            const formatted = `${mins}:${String(secs).padStart(2, '0')}`;
                                            return <span key={key} className="mr-2">{key}: {formatted}</span>;
                                          })}
                                        </div>
                                      )}
                                    </dd>
                                  </div>
                                )}
                              </dl>
                            </div>
                          )}

                          {/* Recent Activity (from context_snapshot) */}
                          {ctx.recent_activity && (
                            <div className="rounded-lg border border-gray-800 bg-black/40 p-3">
                              <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">Recent Activity</h4>
                              <dl className="space-y-2 text-sm">
                                {ctx.recent_activity.chats_last_7d !== undefined && (
                                  <div>
                                    <dt className="text-gray-500 text-xs">Chats (Last 7 days)</dt>
                                    <dd className="text-gray-200">{ctx.recent_activity.chats_last_7d}</dd>
                                  </div>
                                )}
                                {ctx.recent_activity.workouts_logged !== undefined && (
                                  <div>
                                    <dt className="text-gray-500 text-xs">Workouts Logged</dt>
                                    <dd className="text-gray-200">{ctx.recent_activity.workouts_logged}</dd>
                                  </div>
                                )}
                                {ctx.recent_activity.message_samples && Array.isArray(ctx.recent_activity.message_samples) && ctx.recent_activity.message_samples.length > 0 && (
                                  <div>
                                    <dt className="text-gray-500 text-xs mb-2">Message Samples</dt>
                                    <dd className="text-gray-200">
                                      <ul className="space-y-1">
                                        {ctx.recent_activity.message_samples.map((msg, idx) => (
                                          <li key={idx} className="text-xs border-l-2 border-gray-700 pl-2">
                                            <div className="text-gray-300">{msg.preview}</div>
                                            {msg.timestamp && (
                                              <div className="text-gray-500 text-xs mt-0.5">
                                                {new Date(msg.timestamp).toLocaleString(undefined, {
                                                  dateStyle: 'short',
                                                  timeStyle: 'short',
                                                })}
                                              </div>
                                            )}
                                          </li>
                                        ))}
                                      </ul>
                                    </dd>
                                  </div>
                                )}
                              </dl>
                            </div>
                          )}

                          {/* Fallback for any other unhandled keys */}
                          {(() => {
                            const handledKeys = ['ui', 'ui_context', 'client', 'account', 'workout', 'conversation', 'conversation_context', 'coach_persona', 'user_profile', 'recent_activity'];
                            const unhandledEntries = Object.entries(ctx).filter(([key]) => !handledKeys.includes(key));
                            
                            if (unhandledEntries.length === 0) {
                              return null;
                            }
                            
                            const unhandledData = Object.fromEntries(unhandledEntries);
                            
                            // Try to format each unhandled field nicely
                            const hasSimpleFields = unhandledEntries.some(([_, value]) => 
                              typeof value !== 'object' || value === null || Array.isArray(value)
                            );
                            
                            return (
                              <details className="rounded-lg border border-gray-800 bg-black/40 p-3">
                                <summary className="text-xs font-semibold uppercase tracking-wide text-gray-400 cursor-pointer hover:text-gray-300">
                                  Additional Context {hasSimpleFields ? `(${unhandledEntries.length} field${unhandledEntries.length !== 1 ? 's' : ''})` : '(Raw JSON)'}
                                </summary>
                                {hasSimpleFields ? (
                                  <dl className="mt-3 space-y-2 text-sm">
                                    {unhandledEntries.map(([key, value]) => (
                                      <div key={key} className="rounded-md bg-black/60 p-2">
                                        <dt className="text-xs text-gray-500 mb-1 capitalize">{key.replace(/_/g, ' ')}</dt>
                                        <dd className="text-xs text-gray-200 break-words">
                                          {value === null || value === undefined ? (
                                            <span className="text-gray-500 italic">null</span>
                                          ) : Array.isArray(value) ? (
                                            <ul className="list-disc list-inside space-y-1">
                                              {value.map((item, idx) => (
                                                <li key={idx} className="font-mono text-xs">
                                                  {typeof item === 'object' ? JSON.stringify(item) : String(item)}
                                                </li>
                                              ))}
                                            </ul>
                                          ) : typeof value === 'object' ? (
                                            <pre className="whitespace-pre-wrap text-xs font-mono">{prettyJson(value)}</pre>
                                          ) : (
                                            String(value)
                                          )}
                                        </dd>
                                      </div>
                                    ))}
                                  </dl>
                                ) : (
                                  <pre className="mt-3 max-h-64 overflow-auto rounded bg-black/60 p-3 text-xs text-gray-300">
                                    {prettyJson(unhandledData)}
                                  </pre>
                                )}
                              </details>
                            );
                          })()}
                        </>
                      );
                    })()}
                  </div>
                </SectionCard>
              )}

              {!!submission.notes?.length && (
                <SectionCard title="Internal Notes">
                  <ul className="space-y-3">
                    {submission.notes.map((note) => (
                      <li key={note.id} className="rounded-lg border border-gray-800 bg-gray-900/60 p-3">
                        <div className="flex items-center justify-between text-xs text-gray-400">
                          <span>{note.author}</span>
                          <span>
                            {new Date(note.created_at).toLocaleString(undefined, {
                              dateStyle: 'short',
                              timeStyle: 'short',
                            })}
                          </span>
                        </div>
                        <p className="mt-2 text-sm text-gray-100 whitespace-pre-wrap">{note.body}</p>
                      </li>
                    ))}
                  </ul>
                </SectionCard>
              )}

              {!!submission.history?.length && (
                <SectionCard title="Workflow History">
                  <div className="space-y-3">
                    {submission.history.map((entry, index) => {
                      const isStatusChange = entry.action === 'status_changed';
                      const statusFrom = entry.payload?.from;
                      const statusTo = entry.payload?.to;
                      
                      const getStatusColor = (status) => {
                        const statusLower = String(status).toLowerCase();
                        if (statusLower === 'new') return 'bg-blue-500/20 text-blue-300 border-blue-500/50';
                        if (statusLower === 'in_progress') return 'bg-yellow-500/20 text-yellow-300 border-yellow-500/50';
                        if (statusLower === 'resolved') return 'bg-green-500/20 text-green-300 border-green-500/50';
                        if (statusLower === 'archived') return 'bg-gray-500/20 text-gray-300 border-gray-500/50';
                        return 'bg-purple-500/20 text-purple-300 border-purple-500/50';
                      };
                      
                      return (
                        <div key={entry.id} className="relative flex gap-4">
                          {/* Timeline line */}
                          {index < submission.history.length - 1 && (
                            <div className="absolute left-2 top-8 bottom-0 w-0.5 bg-gray-800" />
                          )}
                          
                          {/* Timeline dot */}
                          <div className="relative z-10 flex h-4 w-4 shrink-0 items-center justify-center">
                            <div className="h-2 w-2 rounded-full bg-gray-600 ring-2 ring-gray-900" />
                          </div>
                          
                          {/* Content */}
                          <div className="min-w-0 flex-1 space-y-2 rounded-lg border border-gray-800 bg-black/40 p-4">
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <h4 className="font-semibold text-white capitalize">
                                    {entry.action.replace(/_/g, ' ')}
                                  </h4>
                                  {isStatusChange && statusTo && (
                                    <div className="flex items-center gap-2">
                                      {statusFrom && (
                                        <>
                                          <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${getStatusColor(statusFrom)}`}>
                                            {String(statusFrom).replace(/_/g, ' ')}
                                          </span>
                                          <svg className="h-3 w-3 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                          </svg>
                                        </>
                                      )}
                                      <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${getStatusColor(statusTo)}`}>
                                        {String(statusTo).replace(/_/g, ' ')}
                                      </span>
                                    </div>
                                  )}
                                </div>
                                
                                {entry.payload && !isStatusChange && (
                                  <div className="mt-2 space-y-1.5 rounded-md bg-gray-900/50 p-2">
                                    {typeof entry.payload === 'object' && entry.payload !== null ? (
                                      Object.entries(entry.payload).map(([key, value]) => (
                                        <div key={key} className="flex items-start gap-2 text-xs">
                                          <span className="text-gray-500 capitalize shrink-0">
                                            {key.replace(/_/g, ' ')}:
                                          </span>
                                          <span className="text-gray-200 break-words">
                                            {key === 'status' || key === 'to' || key === 'from' ? (
                                              <span className="capitalize">{String(value)}</span>
                                            ) : typeof value === 'object' ? (
                                              <span className="font-mono text-xs">{JSON.stringify(value)}</span>
                                            ) : (
                                              String(value)
                                            )}
                                          </span>
                                        </div>
                                      ))
                                    ) : (
                                      <pre className="whitespace-pre-wrap text-xs text-gray-400 font-mono">{prettyJson(entry.payload)}</pre>
                                    )}
                                  </div>
                                )}
                              </div>
                              
                              <div className="flex shrink-0 flex-col items-end gap-1 text-xs">
                                <time className="text-gray-400 whitespace-nowrap">
                                  {new Date(entry.created_at).toLocaleDateString(undefined, { 
                                    month: 'short', 
                                    day: 'numeric', 
                                    year: 'numeric' 
                                  })}
                                </time>
                                <time className="text-gray-500 whitespace-nowrap">
                                  {new Date(entry.created_at).toLocaleTimeString(undefined, { 
                                    hour: 'numeric', 
                                    minute: '2-digit' 
                                  })}
                                </time>
                              </div>
                            </div>
                            
                            <div className="flex items-center gap-2 pt-2 border-t border-gray-800">
                              <svg className="h-3 w-3 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                              <span className="text-xs text-gray-500">{entry.actor}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </SectionCard>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FeedbackDetailDrawer;

